import { ChargeEvent, FillUp } from "@fairfleet/geotab";
import { DetectedEvent } from "../models/AuditTrip";
import { knownLocation } from "./tripConsumption";

interface Window {
  start: Date;
  stop: Date;
}

const METRES_PER_KM = 1000;

/**
 * Geotab's detected fill-ups and charging sessions that belong to a trip: the vehicle is
 * refuelled or charged while it stands still, so everything from the trip's start until the next
 * trip starts counts for this trip. The last trip takes what follows it until the end of the
 * loaded period. A tank filled before the first loaded trip is not attributed to any trip.
 */
export function detectedEventsForTrip(
  trip: Window,
  fillUps: FillUp[] = [],
  chargeEvents: ChargeEvent[] = [],
  nextTripStart: Date | undefined,
  periodEnd: Date,
): DetectedEvent[] {
  const end = nextTripStart ? nextTripStart.getTime() - 1 : periodEnd.getTime();
  const inWindow = (time: Date) => time.getTime() >= trip.start.getTime() && time.getTime() <= end;
  const events: DetectedEvent[] = [];
  for (const fillUp of fillUps) {
    if (!inWindow(fillUp.dateTime)) continue;
    if (typeof fillUp.volume !== "number" || !Number.isFinite(fillUp.volume) || fillUp.volume <= 0) continue;
    events.push({
      kind: "fillUp",
      dateTime: fillUp.dateTime,
      amount: fillUp.volume,
      location: knownLocation(fillUp.location),
      odometerKm: typeof fillUp.odometer === "number" && fillUp.odometer > 0 ? fillUp.odometer / METRES_PER_KM : undefined,
      estimated: false,
    });
  }
  for (const charge of chargeEvents) {
    if (!inWindow(charge.startTime)) continue;
    if (typeof charge.energyConsumedKwh !== "number" || !Number.isFinite(charge.energyConsumedKwh) || charge.energyConsumedKwh <= 0) continue;
    events.push({
      kind: "charge",
      dateTime: charge.startTime,
      amount: charge.energyConsumedKwh,
      location: knownLocation(charge.location),
      odometerKm: typeof charge.chargingStartedOdometerKm === "number" && charge.chargingStartedOdometerKm > 0 ? charge.chargingStartedOdometerKm : undefined,
      estimated: !!charge.chargeIsEstimated,
    });
  }
  return events.sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
}

/** Litres or kWh over the events; undefined when there is none. */
export function detectedAmount(events: DetectedEvent[]): number | undefined {
  if (events.length === 0) return undefined;
  return events.reduce((sum, event) => sum + event.amount, 0);
}

/**
 * The refuelled amount shown for a digital entry: the litres of its fuel transactions, or when
 * there is none, Geotab's detected fill-ups or charging sessions. The detection only stands in,
 * it is never added to a transaction.
 */
export function refuelledAmount(
  transactionLiters: number | undefined,
  events: DetectedEvent[],
): { amount?: number; fromDetected: boolean } {
  if (transactionLiters != null) return { amount: transactionLiters, fromDetected: false };
  const detected = detectedAmount(events);
  return { amount: detected, fromDetected: detected != null };
}
